import { useState, useRef, useEffect } from 'react';
import { User, Moon, Sun, Monitor, ChevronRight, CreditCard, Keyboard, LogOut, FileText } from 'lucide-react';
import { RadioButton } from '../../components/RadioButton';
import { useContextoTema } from '../../../contexts/ContextoTema';
import { PlanStatusCard } from './PlanStatusCard';
import { PLAN_COLORS } from './planColors';
import type { FacturacionElectronicaEstado, UserMenuProps } from './types';

const ANIMATION_MS = 150;

/**
 * Badge de estado a la medida de esta fila del menú (20px de contenido).
 * No es el Badge de contasis: su altura mínima (22px, pensado para tablas/cards)
 * haría crecer esta fila puntual respecto a las demás del menú.
 */
const ESTADO_BADGE_STYLES: Record<FacturacionElectronicaEstado, string> = {
  pendiente: 'bg-[var(--warning-bg)] text-[var(--warning)] border-[var(--warning-border)]',
  enProceso: 'bg-[var(--info-bg)] text-[var(--info)] border-[var(--info-border)]',
  habilitada: 'bg-[var(--success-bg)] text-[var(--success)] border-[var(--success-border)]',
};

const ESTADO_BADGE_LABELS: Record<FacturacionElectronicaEstado, string> = {
  pendiente: 'Pendiente',
  enProceso: 'En proceso',
  habilitada: 'Habilitada',
};

function EstadoBadge({ estado }: { estado: FacturacionElectronicaEstado }) {
  return (
    <span
      className={`ml-auto inline-flex h-[20px] flex-none items-center whitespace-nowrap rounded-full border px-2 text-[11px] font-medium leading-none ${ESTADO_BADGE_STYLES[estado]}`}
    >
      {ESTADO_BADGE_LABELS[estado]}
    </span>
  );
}

function getInitials(name: string): string {
  const partes = name.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export const UserMenu = ({
  user,
  planStatus,
  facturacionElectronicaEstado,
  onElectronicInvoicingClick,
  onProfileClick,
  onSettingsClick,
  onHelpClick,
  onPersonalSettingsClick,
  onSwitchAccountClick,
  onSubscriptionsClick,
  onShowKeyboardShortcuts,
  onLogoutClick
}: UserMenuProps) => {
  const { tema, establecerTema } = useContextoTema();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [shouldRenderMenu, setShouldRenderMenu] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
        setIsThemeMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Controla montaje/animación de entrada y salida del dropdown
  useEffect(() => {
    if (isUserMenuOpen) {
      setShouldRenderMenu(true);
      const raf = requestAnimationFrame(() => setIsMenuVisible(true));
      return () => cancelAnimationFrame(raf);
    }

    setIsMenuVisible(false);
    const timeout = setTimeout(() => setShouldRenderMenu(false), ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isUserMenuOpen]);

  const initials = getInitials(user.name);
  const headerBackground = planStatus ? PLAN_COLORS[planStatus.plan].header : undefined;

  return (
    <div className="relative flex items-center" ref={userMenuRef}>
      <button
        className={`w-9 h-9 flex items-center justify-center rounded-full overflow-hidden transition-all duration-200 ${
          isUserMenuOpen
            ? 'ring-2 ring-primary ring-offset-2'
            : 'hover:ring-2 hover:ring-default hover:ring-offset-2'
        }`}
        aria-label="Menú de usuario"
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
      >
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="w-full h-full flex items-center justify-center bg-[var(--primary)] text-[var(--primary-light)] text-xs font-semibold">
            {initials}
          </span>
        )}
      </button>
 
      {/* Dropdown Menu */}
      {shouldRenderMenu && (
        <div
          className={`absolute top-full right-0 mt-2 w-[328px] bg-surface-0 border border-secondary rounded-xl z-50 transition-all duration-150 ease-out ${
            isMenuVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
          }`}
          style={{
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'visible'
          }}
        >
          {/* User Info Header — con o sin gradiente de plan */}
          <div
            className={`px-4 py-4 rounded-t-xl overflow-hidden relative ${headerBackground ? '' : 'bg-surface-1'}`}
            style={headerBackground ? { background: headerBackground } : undefined}
          >
            {headerBackground && (
              <div
                className="absolute -top-10 -right-8 w-[130px] h-[130px] rounded-full pointer-events-none"
                style={{ background: 'rgba(255,255,255,.18)' }}
              />
            )}
            <div className="relative flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-14 h-14 rounded-full overflow-hidden shrink-0 ${
                    headerBackground ? 'shadow-[0_0_0_3px_rgba(255,255,255,.5)]' : 'border-2 border-primary'
                  }`}
                >
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span
                      className={`w-full h-full flex items-center justify-center text-base font-semibold ${
                        headerBackground ? 'bg-black/20 text-white' : 'bg-surface-1 text-primary'
                      }`}
                    >
                      {initials}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0 ml-1">
                  <div className={`text-base font-semibold truncate ${headerBackground ? 'text-white' : 'text-primary'}`}>
                    {user.name}
                  </div>
                  <div className={`text-sm truncate ${headerBackground ? 'text-white/85' : 'text-tertiary'}`}>
                    {user.email}
                  </div>
                  <div
                    className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                      headerBackground ? 'bg-white/85 text-[#065F52] font-semibold' : 'text-tertiary bg-surface-hover'
                    }`}
                  >
                    {user.role}
                  </div>
                </div>
              </div>

              {planStatus && <PlanStatusCard {...planStatus} />}
            </div>
          </div>
 
          {/* Menu Items */}
          <div className="py-1 overflow-hidden">
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => {
                onElectronicInvoicingClick?.();
                setIsUserMenuOpen(false);
              }}
            >
              <FileText size={18} className="shrink-0" />
              <span>Facturación Electrónica</span>
              {facturacionElectronicaEstado && <EstadoBadge estado={facturacionElectronicaEstado} />}
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-muted"></div>

          <div className="py-1" style={{ overflow: 'visible' }}>
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => {
                onProfileClick?.();
                setIsUserMenuOpen(false);
              }}
            >
              <User size={18} className="shrink-0" />
              <span>Perfil</span>
            </button>

            {/* Configuración de la cuenta — oculto hasta MVP 2
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => {
                onSettingsClick?.();
                setIsUserMenuOpen(false);
              }}
            >
              <Settings size={18} className="shrink-0" />
              <span>Configuración de la cuenta</span>
            </button>
            */}

            {/* Theme Submenu */}
            <div className="relative" style={{ overflow: 'visible' }}>
              <button
                className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
                onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
              >
                <Moon size={18} className="shrink-0" />
                <span className="flex-1">Tema</span>
                <ChevronRight size={16} className="shrink-0" />
              </button>
 
              {isThemeMenuOpen && (
                <div
                  className="absolute right-full top-0 mr-1 w-56 bg-surface-1 border border-secondary rounded-lg py-2"
                  style={{
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                    zIndex: 60
                  }}
                >
                  {/* Opción Claro */}
                  <button
                    className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
                    onClick={() => {
                      establecerTema('light');
                      setIsThemeMenuOpen(false);
                    }}
                  >
                    <RadioButton
                      name="theme"
                      value="light"
                      checked={tema === 'light'}
                      onChange={() => {}}
                    />
                    <div className="flex items-center justify-center w-8 h-8 rounded-md border-2 border-secondary bg-surface-0">
                      <Sun size={20} className="text-primary" />
                    </div>
                    <span className="flex-1">Claro</span>
                  </button>
 
                  {/* Opción Oscuro */}
                  <button
                    className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
                    onClick={() => {
                      establecerTema('dark');
                      setIsThemeMenuOpen(false);
                    }}
                  >
                    <RadioButton
                      name="theme"
                      value="dark"
                      checked={tema === 'dark'}
                      onChange={() => {}}
                    />
                    <div className="flex items-center justify-center w-8 h-8 rounded-md border-2 border-secondary bg-surface-0">
                      <Moon size={20} className="text-primary" />
                    </div>
                    <span className="flex-1">Oscuro</span>
                  </button>
 
                  {/* Opción Sistema */}
                  <button
                    className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
                    onClick={() => {
                      establecerTema('system');
                      setIsThemeMenuOpen(false);
                    }}
                  >
                    <RadioButton
                      name="theme"
                      value="system"
                      checked={tema === 'system'}
                      onChange={() => {}}
                    />
                    <div className="flex items-center justify-center w-8 h-8 rounded-md border-2 border-secondary bg-surface-0 overflow-hidden">
                      <Monitor size={20} className="text-primary" />
                    </div>
                    <span className="flex-1">Sistema</span>
                  </button>
                </div>
              )}
            </div>
          </div>
 
          {/* Divider */}
          <div className="border-t border-muted"></div>
 
          <div className="py-1 overflow-hidden">
            {/* Soporte y atención — oculto hasta que se defina el destino
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <LifeBuoy size={18} className="shrink-0" />
              <span>Soporte y atención</span>
            </button>
            */}
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => { onSubscriptionsClick?.(); setIsUserMenuOpen(false); }}
            >
              <CreditCard size={18} className="shrink-0" />
              <span>Mis suscripciones</span>
            </button>
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => { onShowKeyboardShortcuts?.(); setIsUserMenuOpen(false); }}
            >
              <Keyboard size={18} className="shrink-0" />
              <span>Atajos de teclado</span>
            </button>
          </div>
          <div className="border-t border-muted"></div>

          {/* Configuración personal — oculto hasta MVP 2
          <div className="py-1 overflow-hidden">
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => { onPersonalSettingsClick?.(); setIsUserMenuOpen(false); }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <span>Configuración personal</span>
            </button>
          </div>
          <div className="border-t border-muted"></div>
          */}

          {/* Cambiar cuenta — oculto hasta MVP 2
          <div className="py-1 overflow-hidden">
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => { onSwitchAccountClick?.(); setIsUserMenuOpen(false); }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span>Cambiar cuenta</span>
            </button>
          </div>
          <div className="border-t border-muted"></div>
          */}

          {/* Cerrar sesión */}
          <div className="py-1 overflow-hidden rounded-b-xl">
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-error hover:bg-surface-hover transition-colors flex items-center gap-3"
              onClick={() => {
                onLogoutClick?.();
                setIsUserMenuOpen(false);
              }}
            >
              <LogOut size={18} className="shrink-0" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};